import { useEffect, useRef } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { ToastContainer } from 'react-toastify';
import ScrollToTop from './components/_common/ScrollToTop';
import './App.css';
import CookieComponent from './components/_common/CookieComponent';
import NotFoundPage from './pages/NotFoundPage/NotFoundPage';
import Furniture from './pages/Furniture/Furniture';
import Collections from './pages/Collections/Collections';
import Producers from './pages/Producers/Producers';
import Premises from './pages/Premises/Premises';
import Promotions from './pages/Promotions/Promotions';
import Contact from './pages/Contact/Contact';
import HomePage from './pages/HomePage/HomePage';
import PieceOfFurniture from './pages/PieceOfFurniture/PieceOfFurniture';
import SignIn from './pages/SignIn/SignIn';
import AdminPanel from './pages/_AdminPages/AdminPanel/AdminPanel';
import ImportedFurniture from './pages/ImportedFurniture/ImportedFurniture';
import SingleImportedFurniture from './pages/SingleImportedFurniture/SingleImportedFurniture';
import CartPage from './pages/CartPage/CartPage';
import { useCart } from './hooks/useCart';
import PromotionalPieceOfFurniture from './pages/PromotionalPieceOfFurniture/PromotionalPieceOfFurniture';
import { useUser } from './hooks/useUser';
import TransitionsModal from './components/_common/Modal';
import Clarnet from './pages/_AdminPages/AdminPanel/Clarnet/Clarnet';
import ModuleEditor from './pages/_AdminPages/ModuleEditor/ModuleEditor';
import MissingDimensionModuleEditor from './pages/_AdminPages/ModuleEditor/MissingDimensionModuleEditor';
import ValuationPage from './pages/ValuationPage/ValuationPage';
import CustomFurniture from './pages/CustomFurniture/CustomFurniture';
import Complaint from './pages/Complaint/Complaint';
import InvoicePage from './pages/InvoicePage/InvoicePage';
import InvoiceLoginPanel from './pages/InvoiceLoginPanel';
import InvoicesPage from './pages/InvoicesPage/InvoicesPage';
import GlobalLoading from './components/_common/GlobalLoading';
import TimeTracker from './pages/TimeTracker/TimeTracker';
import EmployeesPage from './pages/_AdminPages/Employees/EmployeesPage';
import RfidTagsPage from './pages/_AdminPages/Rfid/RfidTagsPage';
import NotificationsPage from './pages/_AdminPages/Notifications/NotificationsPage';
import NonWorkingDays from './pages/_AdminPages/NonWorkingDays/NonWorkingDays';
import FactoryDeliveryNotices from './pages/_AdminPages/FactoryDeliveryNotices/FactoryDeliveryNotices';
import FactoryDeliveryNoticeBoard from './pages/FactoryDeliveryNoticeBoard/FactoryDeliveryNoticeBoard';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import UsersPage from './pages/_AdminPages/Users/UsersPage';
import ChangePasswordPage from './pages/Auth/ChangePasswordPage';
import KiosksPage from './pages/_AdminPages/Kiosks/KiosksPage';


const theme = createTheme({
  palette: {
    mode: 'light',
  },
});

function App() {

  const cart = useRef(useCart()).current;
  const user = useRef(useUser()).current;

  useEffect(() => {
    cart.get()
    user.autoLogin()
  }, [cart, user]);

  return (
    <ThemeProvider theme={theme}>
      <div className="generalView">

        <CookieComponent />
        <ToastContainer />

        <BrowserRouter>
          <ScrollToTop />
          <Routes>

            <Route
              path='/'
              element={<HomePage />}
            >
            </Route>

            <Route
              path='/meble'
              element={<Furniture />}
            />

            <Route
              path='/meble-na-wymiar'
              element={<CustomFurniture />}
            />

            <Route
              path='/meble-importowane'
              element={<ImportedFurniture />}
            />

            <Route
              path='/kolekcje'
              element={<Collections />}
            />

            <Route
              path='/producenci'
              element={<Producers />}
            />

            <Route
              path='/pomieszczenia'
              element={<Premises />}
            />

            <Route
              path='/promocje'
              element={<Promotions />}
            />

            <Route
              path='/kontakt'
              element={<Contact />}
            />

            <Route
              path='/reklamacja'
              element={<Complaint />}
            />

            <Route
              path='/adminPanel'
              element={
                <ProtectedRoute permission="admin.access"><AdminPanel /></ProtectedRoute>
              }
            />

            <Route
              path='/mebel/:furnitureId'
              element={<PieceOfFurniture />}
            />

            <Route
              path='/mebel-promocyjny/:furnitureId'
              element={<PromotionalPieceOfFurniture />}
            />

            <Route
              path='mebel-importowany/:symbol'
              element={<SingleImportedFurniture />}
            />

            <Route
              path='/logowanie'
              element={<SignIn />}
            />

            <Route path='/odzyskaj-haslo' element={<ForgotPasswordPage />} />
            <Route path='/reset-hasla' element={<ResetPasswordPage />} />
            <Route path='/zmien-haslo' element={<ChangePasswordPage />} />

            <Route
              path='/koszyk'
              element={<CartPage />}
            />


            <Route
              path='/clarnet'
              element={
                <ProtectedRoute permission="furniture.create"><Clarnet /></ProtectedRoute>
              }
            />


            <Route
              path='/moduleEditor'
              element={
                <ProtectedRoute permission="furniture.update"><ModuleEditor /></ProtectedRoute>
              }
            />


            <Route
              path='/missingDimensionModuleEditor'
              element={
                <ProtectedRoute permission="furniture.update"><MissingDimensionModuleEditor /></ProtectedRoute>
              }
            />


            <Route
              path='/wycena/:furnitureId/:valuationId'
              element={<ValuationPage />}
            />


            <Route
              path='/proformy'
              element={<ProtectedRoute permission="invoices.read"><InvoicesPage /></ProtectedRoute>}
            />


            <Route
              path='/proforma/:invoiceId?'
              element={<ProtectedRoute permission="invoices.read"><InvoicePage /></ProtectedRoute>}
            />


            <Route
              path='/logowanieProformy'
              element={<InvoiceLoginPanel />}
            />


            <Route
              path='/skanowanieObecnosci'
              element={<ProtectedRoute permission="timeTracking.read"><TimeTracker /></ProtectedRoute>}
            />

            <Route
              path='/pracownicy'
              element={<ProtectedRoute permission="employees.read"><EmployeesPage /></ProtectedRoute>}
            />

            <Route
              path='/rfid'
              element={<ProtectedRoute permission="rfid.read"><RfidTagsPage /></ProtectedRoute>}
            />

            <Route
              path='/admin/powiadomienia'
              element={<ProtectedRoute permission="notifications.read"><NotificationsPage /></ProtectedRoute>}
            />

            <Route
              path='/admin/wolneFabryk'
              element={<ProtectedRoute permission="nonWorkingDays.read"><NonWorkingDays /></ProtectedRoute>}
            />

            <Route
              path='/admin/awizacjeDostaw'
              element={<ProtectedRoute permission="deliveryNotices.read"><FactoryDeliveryNotices /></ProtectedRoute>}
            />

            <Route path='/admin/uzytkownicy' element={<ProtectedRoute permission="users.read"><UsersPage /></ProtectedRoute>} />
            <Route path='/admin/kioski' element={<ProtectedRoute permission="kiosks.read"><KiosksPage /></ProtectedRoute>} />

            <Route
              path='/awizacje'
              element={<FactoryDeliveryNoticeBoard />}
            />


            <Route path='*' element={<NotFoundPage />} />

          </Routes>
        </BrowserRouter>

        <TransitionsModal />

        <GlobalLoading />

      </div>
    </ThemeProvider>
  );
}

export default App;
